import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LoanInput, LoanUpdate } from '@app/shared';
import {
  archiveLoan,
  createLoan,
  fetchLoan,
  fetchLoans,
  fetchSchedule,
  generateSchedule,
  updateLoan,
} from './api';

const loansKey = (propertyId: number) => ['properties', propertyId, 'loans'] as const;
const loansListKey = (propertyId: number, includeArchived: boolean) =>
  [...loansKey(propertyId), { includeArchived }] as const;
const loanKey = (propertyId: number, loanId: number) => [...loansKey(propertyId), loanId] as const;
const scheduleKey = (propertyId: number, loanId: number) => [...loanKey(propertyId, loanId), 'schedule'] as const;

export const loansQueryOptions = (propertyId: number, includeArchived = false) =>
  queryOptions({
    queryKey: loansListKey(propertyId, includeArchived),
    queryFn: () => fetchLoans(propertyId, includeArchived),
  });

export const loanQueryOptions = (propertyId: number, loanId: number) =>
  queryOptions({ queryKey: loanKey(propertyId, loanId), queryFn: () => fetchLoan(propertyId, loanId) });

export const scheduleQueryOptions = (propertyId: number, loanId: number) =>
  queryOptions({ queryKey: scheduleKey(propertyId, loanId), queryFn: () => fetchSchedule(propertyId, loanId) });

export function useLoans(propertyId: number, includeArchived = false) {
  return useQuery(loansQueryOptions(propertyId, includeArchived));
}

export function useLoan(propertyId: number, loanId: number) {
  return useQuery(loanQueryOptions(propertyId, loanId));
}

export function useSchedule(propertyId: number, loanId: number) {
  return useQuery(scheduleQueryOptions(propertyId, loanId));
}

export function useCreateLoan(propertyId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LoanInput) => createLoan(propertyId, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: loansKey(propertyId) }),
  });
}

export function useUpdateLoan(propertyId: number, loanId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LoanUpdate) => updateLoan(propertyId, loanId, input),
    onSuccess: (loan) => {
      queryClient.setQueryData(loanKey(propertyId, loanId), loan);
      queryClient.invalidateQueries({ queryKey: loansKey(propertyId) });
    },
  });
}

export function useArchiveLoan(propertyId: number, loanId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (archived: boolean) => archiveLoan(propertyId, loanId, archived),
    onSuccess: (loan) => {
      queryClient.setQueryData(loanKey(propertyId, loanId), loan);
      queryClient.invalidateQueries({ queryKey: loansKey(propertyId) });
    },
  });
}

export function useGenerateSchedule(propertyId: number, loanId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => generateSchedule(propertyId, loanId),
    onSuccess: (schedule) => {
      queryClient.setQueryData(scheduleKey(propertyId, loanId), schedule);
      queryClient.invalidateQueries({ queryKey: loanKey(propertyId, loanId) });
      // `hasSchedule` change doit aussi se refléter dans la liste des prêts du bien.
      queryClient.invalidateQueries({ queryKey: loansKey(propertyId) });
    },
  });
}
